// result-verifier-scope-checks.ts
//
// Glob/allowed_paths scope checks split out of result-verifier-checks.ts to
// keep that file ≤200 lines. Leaf module: no dependency on the rest of the
// verifier's checks.

const GLOB_TOKEN_RE = /\*\*|\*|[.+^${}()|[\]\\]/g;

/** Very small glob matcher: supports `**` (any depth) and `*` (one segment), enough for allowed_paths/report_glob shapes used here. */
export function matchesGlob(pattern: string, candidate: string): boolean {
  if (pattern === "**") return true;
  // Single-pass token replacement (no intermediate placeholder string) so
  // "**" and "*" can never collide with each other or with escaped literals.
  const escaped = pattern.replace(GLOB_TOKEN_RE, (token) => {
    if (token === "**") return ".*";
    if (token === "*") return "[^/]*";
    return `\\${token}`;
  });
  return new RegExp(`^${escaped}$`).test(candidate);
}

/**
 * True iff repo-relative `path` falls inside one `allowed_paths` entry.
 *
 * Audit 2026-09-14 finding 5: `allowed_paths` is written once by the
 * controller and read by BOTH this verifier and the pack's PreToolUse hook
 * (`autopilot_binding_common.py::path_in_scope`). The two used to disagree in
 * both directions on the same binding — this side understood `src/**` but
 * rejected the directory-prefix form `src/`, while the hook did the reverse.
 * A false denial blocks a worker from its own declared scope; a false grant
 * waves through what the other layer will reject. So both forms are accepted
 * here, matching `path_matches_allowed_entry` on the hook side entry for
 * entry, pinned by a shared fixture both test suites read.
 */
export function pathAllowed(path: string, allowedPaths: string[]): boolean {
  return allowedPaths.some((pattern) => {
    if (matchesGlob(pattern, path)) return true;
    // Directory-prefix form (`src/`, or a bare `src`): everything at or
    // beneath that path SEGMENT. Segment, never a character prefix — `src/`
    // must not match `srcevil/app.ts`.
    const prefix = pattern.endsWith("/") ? pattern.slice(0, -1) : pattern;
    if (prefix === "") return false;
    return path === prefix || path.startsWith(`${prefix}/`);
  });
}

export function checkChangedFilesInScope(
  changedFiles: string[],
  allowedPaths: string[],
): string | undefined {
  const offenders = changedFiles.filter((f) => !pathAllowed(f, allowedPaths));
  if (offenders.length > 0) {
    return `changed_files vượt allowed_paths (scope): ${offenders.join(", ")}`;
  }
  return undefined;
}
